/**
 * AppHeader Component
 *
 * Fixed 48px header bar with logo, theme toggle, and user menu.
 * Matches the CSP layout pattern adapted with LeftShield branding.
 */
import {
  makeStyles,
  tokens,
  Button,
  Avatar,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  MenuDivider,
  Tooltip,
  Subtitle1,
  Body2,
} from '@fluentui/react-components';
import {
  PersonCircle24Regular,
  SignOut24Regular,
  WeatherMoonRegular,
  WeatherSunnyRegular,
  ShieldCheckmark24Regular,
} from '@fluentui/react-icons';
import { useAuth } from '../../features/authentication';
import { useAppStore } from '../../stores/appStore';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../App';
import { solutionTokens } from '../../theme/solution-theme';

const HEADER_HEIGHT = '48px';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: HEADER_HEIGHT,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: solutionTokens.brandPrimary,
    color: tokens.colorNeutralForegroundStaticInverted,
    boxShadow: tokens.shadow4,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    cursor: 'pointer',
    padding: tokens.spacingHorizontalXS,
    borderRadius: tokens.borderRadiusMedium,
    ':focus-visible': {
      outline: `2px solid ${tokens.colorNeutralForegroundStaticInverted}`,
      outlineOffset: '2px',
    },
  },
  logoIcon: {
    color: tokens.colorNeutralForegroundStaticInverted,
    fontSize: tokens.fontSizeBase600,
  },
  logoText: {
    color: tokens.colorNeutralForegroundStaticInverted,
    fontWeight: tokens.fontWeightSemibold,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerButton: {
    color: tokens.colorNeutralForegroundStaticInverted,
    ':hover': {
      color: tokens.colorNeutralForegroundStaticInverted,
      // rgba(255,255,255,0.15) === the static-inverted (white) token at ~15% alpha (hex 26).
      backgroundColor: `${tokens.colorNeutralForegroundStaticInverted}26`,
    },
  },
  userName: {
    color: tokens.colorNeutralForegroundStaticInverted,
    marginRight: tokens.spacingHorizontalS,
  },
});

export function AppHeader() {
  const styles = useStyles();
  const { user, isAuthenticated, logout } = useAuth();
  const { isMobile } = useAppStore();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className={styles.header} role="banner">
      <div className={styles.leftSection}>
        <div
          className={styles.logo}
          onClick={() => navigate('/dashboard')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard')}
          tabIndex={0}
          role="button"
          aria-label="Go to dashboard"
        >
          <ShieldCheckmark24Regular className={styles.logoIcon} />
          <Subtitle1 className={styles.logoText}>LeftShield</Subtitle1>
        </div>
      </div>

      <div className={styles.rightSection}>
        <Tooltip content={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} relationship="label">
          <Button
            appearance="subtle"
            icon={mode === 'dark' ? <WeatherSunnyRegular /> : <WeatherMoonRegular />}
            className={styles.headerButton}
            onClick={toggleTheme}
            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          />
        </Tooltip>

        {isAuthenticated && user ? (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                className={styles.headerButton}
                icon={
                  <Avatar
                    name={user.displayName}
                    initials={getInitials(user.displayName)}
                    size={28}
                    style={{ cursor: 'pointer' }}
                  />
                }
              >
                {!isMobile && (
                  <Body2 className={styles.userName}>
                    {user.displayName}
                  </Body2>
                )}
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<PersonCircle24Regular />} onClick={() => navigate('/profile')}>
                  Profile
                </MenuItem>
                <MenuDivider />
                <MenuItem icon={<SignOut24Regular />} onClick={handleLogout}>
                  Sign out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : (
          <Button
            appearance="subtle"
            className={styles.headerButton}
            onClick={() => navigate('/login')}
          >
            Sign in
          </Button>
        )}
      </div>
    </header>
  );
}
